import { Box, IconButton } from "@mui/material";
import useGlobalContext from "hooks/globalData";
import useBoolean from "hooks/useBoolean";
import useTrashablePromises from "hooks/useTrashablePromises";
import PreviewDialog from "components/PreviewTemplateDialog";
import { useCallback, useState } from "react";
import {
  QualificationStatement,
  TemplateAdmin,
  TemplatesByPlaceholderName,
} from "services/KnowfullyAdminClient";
import PreviewTemplateIcon from "@mui/icons-material/Visibility";
import { Severity, SnackbarMessageProps } from "types/common-types";
import SnackbarMessage from "components/SnackbarMessage";
import { useIntl } from "react-intl";
import { API_ORGANIZATION_ID } from "config/headers";
import PreviewDataSelectorDialog from "pages/ContentTemplatesPage/PreviewDataSelectorDialog";

type QualificationStatementPreviewProps = {
  qualificationStatement: QualificationStatement;
};

const QualificationStatementPreview = ({
  qualificationStatement,
}: QualificationStatementPreviewProps) => {
  const { formatMessage: t } = useIntl();
  const { registerPromise } = useTrashablePromises();
  const { templateService } = useGlobalContext();

  const [organizationForPreview, setOrganizationForPreview] = useState<
    string | null | undefined
  >(null);

  const [templates, setTemplates] = useState<
    TemplateAdmin[] | null | undefined
  >(null);

  const [organizationIds, setOrganizationIds] = useState<string[] | undefined>();

  const [previewOpen, previewOpenActions] = useBoolean(false);
  const [previewDataSelectorOpen, previewDataSelectorActions] = useBoolean(
    false
  );

  const [hasTemplate, setHasTemplate] = useBoolean(true);

  const [
    snackbarMessage,
    setSnackbarMessage,
  ] = useState<SnackbarMessageProps | null>(null);

  const onSetPreviewData = (organizationId: string) => {
    setOrganizationForPreview(organizationId);
    previewDataSelectorActions.setFalse();
    previewOpenActions.setTrue();
  };

  const handlePreview = useCallback(
    async (e: any) => {
      e.stopPropagation();

      const contentName = qualificationStatement.contentName;

      if (contentName) {
        try {
          const response = await registerPromise(
            templateService.getTemplateByPlaceholderName(contentName)
          );

          const data = response.data as TemplatesByPlaceholderName;

          const {
            templates,
            hasMultipleOrganizations,
            organizationIds,
            hasDefaultTemplate,
          } = data;

          if (
            templates !== null &&
            templates !== undefined &&
            templates.length > 0
          ) {
            setHasTemplate.setTrue();
          } else {
            setHasTemplate.setFalse();
            setSnackbarMessage(
              new SnackbarMessageProps(
                t({ id: "qualificationStatementTemplateNotAvailable" }),
                Severity.Warning
              )
            );
          }

          setTemplates(templates);
          setOrganizationIds(organizationIds);

          if (
            hasMultipleOrganizations ||
            templates?.[0]?.organizationId?.toLowerCase() ===
              API_ORGANIZATION_ID?.toLowerCase() ||
            hasDefaultTemplate
          ) {
            if (hasDefaultTemplate) {
              setOrganizationIds(undefined);
            }
            previewDataSelectorActions.setTrue();
          } else if (templates) {
            setOrganizationForPreview(templates[0].organizationId);
            previewOpenActions.setTrue();
          }
        } catch (error) {}
      }
    },
    [
      qualificationStatement.contentName,
      registerPromise,
      templateService,
      setHasTemplate,
      t,
      previewDataSelectorActions,
      previewOpenActions,
    ]
  );

  return (
    <>
      <Box textAlign="center">
        <IconButton size="small" onClick={handlePreview}>
          <PreviewTemplateIcon />
        </IconButton>
      </Box>

      {templates !== null && templates !== undefined && hasTemplate && (
        <>
          <PreviewDialog
            open={previewOpen}
            handleConfirm={() => previewOpenActions.setFalse()}
            templateVersionId={templates[0].templateVersionId!}
            organizationId={organizationForPreview!}
          />
          <PreviewDataSelectorDialog
            onCancel={previewDataSelectorActions.setFalse}
            open={previewDataSelectorOpen}
            handleConfirm={onSetPreviewData}
            organizationIds={organizationIds}
          />
        </>
      )}

      <SnackbarMessage
        snackbarMessage={snackbarMessage}
        setSnackbarMessage={setSnackbarMessage}
      />
    </>
  );
};

export default QualificationStatementPreview;
